/**
 * PROGRAM COPY GATE
 *
 * DAC designation is an INPUT to certain incentives, not a qualification. Two
 * concrete traps:
 *
 *   - The federal Low-Income Communities Bonus Credit uses New Markets Tax
 *     Credit geography and poverty thresholds, NOT NYSERDA DACs.
 *   - NY-Sun's affordable solar adder is income-qualified through submitted
 *     documentation, not by tract.
 *
 * Copy implying a dollar figure the customer does not receive is exposure under
 * FTC Act 5 and GBL 349-350, and, in practice more sharply, the PSC's Uniform
 * Business Practices for DER Suppliers and NYSERDA's contractor Code of Conduct,
 * which carry program discipline up to loss of participating-contractor status.
 * (This said "16 CFR Part 429 and GBL Article 36-A" until 2026-08-05. Those govern
 * the in-home sale and the signed contract and do NOT reach a web widget; Nico
 * corrected the frame in docs/legal-review-2026-08-05.md. The gate is unchanged,
 * only the reason it cites.) Hence a gate rather than a comment:
 * `tools/check-copy-gate.mjs` fails CI on an inconsistent state, and the widget
 * renders no program claims while PROGRAM_COPY_APPROVED is false.
 *
 * Shipping program copy takes three edits in one commit:
 *
 *   1. PROGRAM_COPY_APPROVED to true.
 *   2. PROGRAM_COPY_REVIEW_DOC to the path or URL of Nico's review. Null, empty
 *      and whitespace-only are rejected.
 *   3. Write the reviewed entries into APPROVED_PROGRAMS.
 *
 * Step 3 is the point: flipping the flag alone promotes nothing, because there is
 * no standby content to reach. An earlier version returned a marked placeholder
 * array from the approved path, putting one boolean between draft text and a
 * customer. The gate now also rejects any entry carrying PLACEHOLDER_MARKER.
 *
 * All three need verified current program-manual language and Nico's review.
 */
export const PROGRAM_COPY_APPROVED = false;

/** Path or URL to the reviewed-copy document. Must be non-blank when APPROVED is true. */
export const PROGRAM_COPY_REVIEW_DOC: string | null = null;

/**
 * Marker every unreviewed draft string must carry, so a leak is obvious on sight.
 * The gate fails the build if this literal, or an interpolation of this constant,
 * appears in APPROVED_PROGRAMS while the copy is marked approved.
 */
export const PLACEHOLDER_MARKER = "[PLACEHOLDER, NOT REVIEWED]";

/**
 * Readonly because this is a legal control and the array below is a module
 * singleton: a stray assignment in a warm Cloud Function instance would rewrite
 * approved copy for every later request on that instance.
 */
export type ProgramCopy = {
  readonly id: string;
  readonly name: string;
  /** What DAC status actually does for this program. */
  readonly role: string;
  /** The conditions still outstanding. Stated plainly, per spec D1. */
  readonly remainingConditions: string;
};

/**
 * Every string here is a claim made to a customer, so every entry needs a
 * corresponding line in the document named by PROGRAM_COPY_REVIEW_DOC.
 */
export const APPROVED_PROGRAMS: readonly ProgramCopy[] = [];

/**
 * The only function the widget may call for program content. Empty until copy is
 * approved: absent, not a stub and not greyed-out text.
 */
export function approvedPrograms(): readonly ProgramCopy[] {
  if (!PROGRAM_COPY_APPROVED) return [];
  return APPROVED_PROGRAMS;
}
